<template>
  <div class="inco-assessment-page chart-content">
    <div class="inco-assessment-wrapper">
      <div ref="print">
        <div class="page-card-wrapper">
          <div class="page-card-title">
            <div
              class="left-side-text"
              v-if="$store.state.patient.selectedPatient"
            >
              {{ $store.state.patient.selectedPatient.alias }}
              <span v-if="$store.state.patient.selectedPatient.podMac"
                >({{
                  $store.state.patient.selectedPatient.podMac.substr(-4)
                }})</span
              >
            </div>
            <span class="page-card-title-text">{{ title }}</span>
            <DateRangePicker />
          </div>
          <div class="page-card-content">
            <div class="inco-assessment-subtitle mt-10">
              {{ $t('text.bladder_chart').toUpperCase() }}
            </div>
            <PrintSection :chartData="calculatedData" :loading="loading" />
            <div class="relative">
              <ChartFilter
                ref="chartFilter"
                :additionalLegends="additionalLegends"
                :expandLegend="expandLegend"
                @updateData="getData()"
              />
              <div class="time-location-text">
                {{ $t('text.all_times_are_in') }} {{ location || '--' }}
              </div>
            </div>
            <SummaryModule
              :summaryData="summaryData"
              :loading="loading"
              v-if="showSummary"
            />
          </div>
        </div>
      </div>
      <div class="action-btns mt-20">
        <div class="action-btn" @click="share()" v-if="showShareBtn">
          <svg-icon
            iconClass="share"
            class="w-15 mr-10"
            className="white-font"
          ></svg-icon>
          <span>{{ $t('text.share') }}</span>
        </div>
        <div class="action-btn" @click="print()" v-if="showPrintBtn">
          <svg-icon
            iconClass="report"
            class="w-15 mr-10"
            className="white-font"
          ></svg-icon>
          <span>{{ $t('text.print') }}</span>
        </div>
      </div>
    </div>
    <ShareDialog ref="shareDialog" />
  </div>
</template>

<script>
import moment from 'moment'
import ApiService from '@/services/ApiService.js'
import ShareDialog from '@/components/patients/charts/ShareDialog'
import { images, voidState, hours } from '@/const.js'
import DateRangePicker from '@/components/patients/charts/DateRangePicker'
import SummaryModule from '@/components/patients/incoAssessment/SummaryModule'
import ChartFilter from '@/components/patients/incoAssessment/ChartFilter'
import PrintSection from '@/components/patients/incoAssessment/PrintSection'
import html2canvas from 'html2canvas'
import printJS from 'print-js'
import $ from 'jquery'
import swal from 'sweetalert'
import {
  padCycleEndType,
  padCycleStartType,
  voidConst,
  bladderEventType,
  observationTypeId
} from '@/components/patients/charts/smartzEventsChart/SmartzEventConst.js'
export default {
  data() {
    return {
      images,
      voidState,
      moment,
      userLang: navigator.language || navigator.userLanguage,
      hours: hours,
      voidData: [],
      assesmentData: [],
      calculatedData: [],
      summaryData: null,
      loading: false,
      padCycleEndType,
      padCycleStartType,
      bladderEventType,
      eventDistanceLessShouldCombine: 30,
      notCombinedEventStatus: [voidConst.external, voidConst.outOfRange],
      dayDuration: 86400,
      location: null,
      additionalLegends: [], // inco assessment chart legends
      observationTypeIdConst: observationTypeId
    }
  },
  components: {
    ShareDialog,
    DateRangePicker,
    PrintSection,
    SummaryModule,
    ChartFilter
  },
  props: {
    title: {
      type: String,
      require: true
    },
    observationTypeId: {
      // if observation chart, then observationTypeId is 0; if Inco Assessment, then observationTypeId is 2
      type: Number,
      require: true
    },
    showSummary: {
      type: Boolean,
      require: false,
      default: true
    },
    expandLegend: {
      type: Boolean,
      require: false,
      default: false
    },
    showShareBtn: {
      type: Boolean,
      require: false,
      default: false
    },
    showPrintBtn: {
      type: Boolean,
      require: false,
      default: false
    }
  },
  watch: {
    '$store.state.patient.selectedPatient.patientId': function() {
      this.getData()
    },
    '$store.state.patient.selectedDateRange': function() {
      this.getData()
    },
    voidData: {
      handler() {
        this.getCalculatedData()
      },
      deep: true
    }
  },
  mounted: function() {
    this.getData()
    this.getLegend()
  },
  computed: {
    subject() {
      return this.title
    },
    selectedFilter() {
      return this.$refs.chartFilter.selectedFilter
    }
  },
  methods: {
    getLegend() {
      ApiService.GetObservationValuesForLegend(
        this.$store.state.locale.currentLocale,
        this.observationTypeId
      ).then(response => {
        this.additionalLegends = response.data.data
      })
    },
    getData() {
      if (!this.$store.state.patient.selectedPatient) return false
      this.loading = true
      ApiService.GetPatientBladderChart(
        this.$store.state.patient.selectedPatient.patientId,
        moment(this.$store.state.patient.selectedDateRange[0]).format(
          'YYYY-MM-DD'
        ),
        moment(this.$store.state.patient.selectedDateRange[1]).format(
          'YYYY-MM-DD'
        ),
        this.$store.state.locale.currentLocale,
        this.observationTypeId
      )
        .then(response => {
          this.loading = false
          if (
            typeof response.data === 'string' &&
            response.data.includes('- demo')
          ) {
            swal({
              icon: 'error',
              title: this.$t('text.failed'),
              text: response.data.replace('- demo', '')
            })
            return false
          }
          this.location = response.data.data.Location
          this.getNightTime(
            response.data.data.nightTimeStart,
            response.data.data.nightTimeEnd
          )
          this.summaryData = response.data.data.summary
          this.voidData = response.data.data.voidData
          this.assesmentData = response.data.data.assesmentData || []

          // if no void data, only have assessement data, then create a virtual pad cycle
          if (this.assesmentData.length && !this.voidData.length) {
            this.voidData.push({
              PadCycleId: 0,
              IsCompleted: 2,
              StartDate: moment(
                this.$store.state.patient.selectedDateRange[0]
              ).format('YYYY-MM-DDT00:00:00'),
              EndDate: moment(
                this.$store.state.patient.selectedDateRange[1]
              ).format('YYYY-MM-DDT23:59:59'),
              PadStatus: 0,
              Voids: []
            })
          }

          // calculate data in the chart
          if (this.voidData.length || this.assesmentData.length) {
            this.getCalculatedData()
          } else {
            this.calculatedData = []
          }
        })
        .catch(() => {
          this.loading = false
          this.voidData = []
          swal({
            icon: 'error',
            title: this.$t('text.failed'),
            text: this.$t('text.failed_to_get_chart_data')
          })
        })
    },
    getCalculatedData() {
      this.calculatedData = []
      let data = this.spliteCrossDayData()
      data = data.map(item => {
        let date = moment(item.StartDate).format('YYYY-MM-DD')
        return {
          ...item,
          Date: date,
          Voids: item.Voids.filter(voidItem => {
            return (
              moment(voidItem.TimeStamp).isSameOrBefore(item.EndDate) &&
              moment(voidItem.TimeStamp).isSameOrAfter(item.StartDate)
            )
          }).map(voidItem => {
            return {
              ...voidItem,
              StartToToday: moment(voidItem.TimeStamp).diff(
                moment(date + ' 00:00:00'),
                'seconds'
              )
            }
          }),
          StartToToday: moment(item.StartDate).diff(
            moment(date + ' 00:00:00'),
            'seconds'
          ),
          BlockWidth: moment(item.EndDate).diff(
            moment(item.StartDate),
            'seconds'
          )
        }
      })

      // group by date
      let startDate = moment(
        this.$store.state.patient.selectedDateRange[0]
      ).format('YYYY-MM-DD')
      let endDate = moment(
        this.$store.state.patient.selectedDateRange[1]
      ).format('YYYY-MM-DD')
      let dateDiff = moment(endDate).diff(startDate, 'days')
      for (let i = 0; i <= dateDiff; i++) {
        let date = moment(startDate)
          .add(i, 'day')
          .format('YYYY-MM-DD')
        this.calculatedData.push({
          Date: date,
          Data: data.filter(item => item.Date === date)
        })
      }
    },
    // Split cross day data so that StartDate and EndDate are on the same day
    spliteCrossDayData() {
      let calculatedData = []
      this.voidData.forEach(item => {
        let dayDiff = moment(
          moment(item.EndDate).format('YYYY-MM-DD 00:00:00')
        ).diff(
          moment(moment(item.StartDate).format('YYYY-MM-DD 00:00:00')),
          'days'
        )

        let voidEvents = this.spliteCrossDayEvent(
          this.addPadStartAndEndEvent(item)
        )
        let assessmentEvents = this.getAssessmentEvent(item)
        let events = voidEvents.concat(assessmentEvents)
        events = events.sort((a, b) =>
          moment(a.TimeStamp).diff(moment(b.TimeStamp))
        )

        item = {
          ...item,
          StartDate: moment(item.StartDate).format('YYYY-MM-DD HH:mm:ss'),
          EndDate: moment(item.EndDate).format('YYYY-MM-DD HH:mm:ss'),
          IsStarted: padCycleStartType.isStart,
          Voids: this.combineEvents(events)
        }

        if (dayDiff === 0) {
          calculatedData.push(item)
        } else {
          for (let i = 0; i <= dayDiff; i++) {
            if (i === 0) {
              calculatedData.push({
                ...item,
                EndDate: moment(item.StartDate).format('YYYY-MM-DD 23:59:59'),
                IsCompleted: padCycleEndType.notCompleted
              })
            } else {
              let startDate = moment(item.StartDate)
                .add(i, 'day')
                .format('YYYY-MM-DD 00:00:00')
              let endDate = moment(item.StartDate)
                .add(i, 'day')
                .format('YYYY-MM-DD 23:59:59')
              calculatedData.push({
                ...item,
                StartDate: startDate,
                EndDate: moment(item.EndDate).isBefore(moment(endDate))
                  ? item.EndDate
                  : endDate,
                IsCompleted: moment(item.EndDate).isBefore(moment(endDate))
                  ? item.IsCompleted
                  : padCycleEndType.notCompleted,
                IsStarted: padCycleStartType.notStart
              })
            }
          }
        }
      })
      return calculatedData
    },
    // Concat assessment icon
    getAssessmentEvent(item) {
      let padStartTime = item.StartDate
      let padEndTime = item.EndDate
      let assesments = this.assesmentData
        .filter(assment => {
          let timeStamp = moment(assment.EntryDateTime).format(
            'YYYY-MM-DD HH:mm:ss'
          )
          // if observation chart, use ParentId to filter; if Inco Assessment, use ObservationId to compare
          let filterId =
            this.observationTypeId === this.observationTypeIdConst.all
              ? assment.ParentId
              : assment.ObservationId
          return (
            moment(timeStamp).isSameOrBefore(padEndTime) &&
            moment(timeStamp).isSameOrAfter(padStartTime) &&
            this.selectedFilter.includes(filterId)
          )
        })
        .map(assment => {
          let timeStamp = moment(assment.EntryDateTime).format(
            'YYYY-MM-DD HH:mm:ss'
          )
          return {
            VoidAmount: null,
            Status: bladderEventType.assessment,
            Duration: 0,
            TimeStamp: timeStamp,
            EndTimeStamp: timeStamp,
            Type: bladderEventType.assessment,
            Details: assment
          }
        })
      return assesments
    },
    // Add start and end icon of pad cycle
    addPadStartAndEndEvent(item) {
      let events = JSON.parse(JSON.stringify(item.Voids))
      // if virtual pad cycle, then don't add start and end event
      if (!item.PadCycleId) return events

      events.unshift({
        Status: voidConst.padStart,
        TimeStamp: item.StartDate
      })
      if (item.IsCompleted !== padCycleEndType.notCompleted) {
        events.push({
          Status: 'PADENDLEVEL' + this.calcPadStatus(item.PadStatus),
          TimeStamp: item.EndDate
        })
      }
      return events
    },
    // Previously, the brief utilization was sent in a set format of 25,50,75 or 100.
    // Now, the utilization will be sent with more accurate numbers. So, use the comparison operator for choosing a brief capacity icon
    calcPadStatus(status) {
      if (status === 0) {
        return 0
      } else if (status > 0 && status <= 25) {
        return 25
      } else if (status > 25 && status <= 50) {
        return 50
      } else if (status > 50 && status <= 75) {
        return 75
      } else {
        return 100
      }
    },
    // Split cross day events. If event cross one day, it will be divided into multiple events according to duration
    spliteCrossDayEvent(events) {
      let calculatedEvents = []
      events.forEach(eventItem => {
        if (!eventItem.Status) return
        let statusArr = eventItem.Status.split('-')
        let duration = statusArr[1] || 0
        eventItem = {
          VoidAmount: eventItem.VoidAmount,
          Status: statusArr[0],
          Duration: duration,
          TimeStamp: moment(eventItem.TimeStamp).format('YYYY-MM-DD HH:mm:ss'),
          EndTimeStamp: moment(eventItem.TimeStamp)
            .add(duration, 'seconds')
            .format('YYYY-MM-DD HH:mm:ss'),
          Type: bladderEventType.void
        }

        let fromEventToDayEnd = moment(
          moment(eventItem.TimeStamp).format('YYYY-MM-DD 23:59:59')
        ).diff(moment(eventItem.TimeStamp), 'seconds')

        if (eventItem.Duration <= fromEventToDayEnd) {
          calculatedEvents.push(eventItem)
        } else {
          let dayDiff = Math.ceil(eventItem.Duration / this.dayDuration)
          for (let i = 0; i <= dayDiff; i++) {
            let currentEvent = {}
            if (i === 0) {
              currentEvent = {
                ...eventItem,
                EndTimeStamp: moment(eventItem.TimeStamp).format(
                  'YYYY-MM-DD 23:59:59'
                )
              }
            } else {
              let timeStamp = moment(eventItem.TimeStamp)
                .add(i, 'day')
                .format('YYYY-MM-DD 00:00:00')
              let endTimeStamp = moment(eventItem.TimeStamp)
                .add(i, 'day')
                .format('YYYY-MM-DD 23:59:59')
              currentEvent = {
                ...eventItem,
                TimeStamp: timeStamp,
                EndTimeStamp: moment(eventItem.EndTimeStamp).isBefore(
                  moment(endTimeStamp)
                )
                  ? eventItem.EndTimeStamp
                  : endTimeStamp
              }
            }
            currentEvent.Duration = moment(
              moment(currentEvent.EndTimeStamp)
            ).diff(moment(currentEvent.TimeStamp), 'seconds')
            calculatedEvents.push(currentEvent)
          }
        }
      })
      return calculatedEvents
    },
    // When the occurrence time of multiple events is less than 10 minutes, then combine these icons
    combineEvents(events) {
      let combinedEvents = []
      for (let i = 0; i < events.length; i++) {
        let currentEvent = events[i]
        if (i === 0) {
          combinedEvents.push({
            ...currentEvent,
            SubEvents: []
          })
        } else {
          let prevEvent = combinedEvents[combinedEvents.length - 1]
          let diffTime = moment(moment(currentEvent.TimeStamp)).diff(
            moment(prevEvent.TimeStamp),
            'minutes'
          )
          if (
            this.notCombinedEventStatus.includes(prevEvent.Status) ||
            this.notCombinedEventStatus.includes(currentEvent.Status) ||
            diffTime > this.eventDistanceLessShouldCombine
          ) {
            combinedEvents.push({
              ...currentEvent,
              SubEvents: []
            })
          } else {
            combinedEvents[combinedEvents.length - 1].SubEvents.push(
              currentEvent
            )
          }
        }
      }
      return combinedEvents
    },
    // Dynamically determine the nighttime time range
    getNightTime(nightTimeStart, nightTimeEnd) {
      nightTimeStart =
        moment().format('YYYY-MM-DD') + ' ' + nightTimeStart.split('.')[0]
      nightTimeEnd =
        moment().format('YYYY-MM-DD') + ' ' + nightTimeEnd.split('.')[0]
      this.hours.forEach(hour => {
        const hourValue = moment().format('YYYY-MM-DD') + ' ' + hour.value
        // Consider whether the night time is short or not
        if (moment(nightTimeStart).isAfter(nightTimeEnd)) {
          hour.active =
            moment(hourValue).isSameOrAfter(nightTimeStart) ||
            moment(hourValue).isBefore(nightTimeEnd)
        } else {
          hour.active =
            moment(hourValue).isSameOrAfter(nightTimeStart) &&
            moment(hourValue).isBefore(nightTimeEnd)
        }
      })
    },
    share() {
      this.$refs.shareDialog.openDialog(
        this.observationTypeId === this.observationTypeIdConst.all
          ? 'observation_chart'
          : 'inco_assessment'
      )
    },
    print() {
      window.pageYoffset = 0
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
      let options = {
        backgroundColor: null,
        dpi: window.devicePixelRatio * 2,
        scale: 2,
        removeContainer: false,
        useCORS: true,
        onclone: doc => {
          $(doc)
            .find('body')
            .css('width', '2200px')
          $(doc)
            .find('.new-design .sidebar-type-page-content')
            .css('width', '100%')
          $(doc)
            .find('.new-design .page-card-wrapper')
            .css('max-width', 'unset')
          $(doc)
            .find('.new-design .page-card-wrapper .page-card-content')
            .css('height', 'auto')
          $(doc)
            .find(
              '.new-design .page-card-wrapper .page-card-title .page-card-title-text'
            )
            .css('margin-top', 0)
          $(doc)
            .find(
              '.new-design .page-card-wrapper .page-card-title .left-side-text'
            )
            .css('display', 'inline-block')
          $(doc)
            .find(
              '.new-design .page-card-wrapper .page-card-title .date-wrapper'
            )
            .css('position', 'absolute')
            .css('top', '6px')
            .css('right', '10px')
          $(doc)
            .find('.el-range-editor .el-range-input')
            .css('line-height', '24px')
          $(doc)
            .find('.inco-assessment-subtitle')
            .css('margin-top', '10px')
        }
      }
      html2canvas(this.$refs.print, options).then(canvas => {
        const url = canvas.toDataURL()
        printJS({
          printable: url,
          type: 'image'
        })
      })
    }
  }
}
</script>
